import { memo, useMemo } from 'react';
import { List } from '@divlab/divanui';

import StringParameter from '../StringParameter';
import SampleParameter from '../SampleParameter';
import ListParameter from '../ListParameter';
import Dimension from '../Dimension';
import Hardness from '../Hardness';
import styles from './Column.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { Parameter } from '@Pages/PageProduct';

type Items = Record<Parameter['theme'], Parameter[]>;

export interface ColumnProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  parameters: Parameter[];
}

const Column: FC<ColumnProps> = (props) => {
  const { parameters, ...restProps } = props;

  const items = useMemo(() => {
    const result: Items = {
      default: [],
      dropdown: [],
      dimension: [],
      circle: [],
      hardness: [],
      mechanism: [],
      list: [],
    };

    parameters.forEach((parameter) => {
      if (!result[parameter.theme]) result[parameter.theme] = [];

      if (parameter.theme === 'dropdown' && ![2, 3].includes(parameter.groupId)) {
        result[parameter.theme].push({
          ...parameter,
          variants: parameter.variants.filter((v) => v.selected),
        });
        return;
      }
      result[parameter.theme].push(parameter);
    });

    return result;
  }, [parameters]);

  return (
    <div {...restProps}>
      {items.dimension.length > 0 && (
        <List
          items={items.dimension}
          renderChild={(parameter: Parameter) => (
            <Dimension
              className={styles.dimension}
              name={parameter.name}
              value={parameter.values}
            />
          )}
        />
      )}

      {items.hardness.length > 0 && (
        <List
          className={styles.hardness}
          items={items.hardness}
          renderChild={(hardness: Parameter) => (
            <Hardness
              name={hardness.name}
              value={hardness.value}
              icon={hardness.icon}
              description={hardness.description}
            />
          )}
        />
      )}
      {[...items.mechanism, ...items.default].map((parameter) => {
        return (
          <StringParameter
            key={parameter.variant || parameter.title}
            className={styles.parameter}
            parameter={parameter}
          />
        );
      })}
      {items.circle.map((parameter) =>
        parameter.variants.map((variant) => {
          return (
            <SampleParameter key={variant.name} className={styles.parameter} material={variant} />
          );
        }),
      )}

      {items.dropdown.map((parameter) =>
        parameter.variants.map((variant) => {
          const material = {
            ...variant,
            name: parameter.name,
            title: variant.name,
          };

          if (!variant.selected) return;

          return (
            <SampleParameter key={variant.id} className={styles.parameter} material={material} />
          );
        }),
      )}
      {items.list.map((parameter) => {
        return (
          <ListParameter
            key={parameter.variant || parameter.name}
            className={styles.parameter}
            parameter={parameter}
          />
        );
      })}
    </div>
  );
};

export default memo(Column);
